import { useId, useState } from "react";
import { PageHeader } from "@/components/PageHeader";
import { AreaTabs, SETUP_TABS } from "@/components/AreaTabs";
import { StateMessage } from "@/components/StateMessage";
import { RowActions } from "@/components/RowActions";
import { usePendingTimeOff, useTimeOffHistory } from "@/hooks/useTimeOff";
import { demoApi, problemMessage } from "@/api/client";
import { formatDateShort } from "@/lib/format";
import type { Guid, TimeOffRequest, TimeOffStatus } from "@/api/types";
import "./TimeOff.css";

/**
 * ef-040-3 (D3) — the owner's Time off screen, `/setup/time-off`: the pending queue
 * (Approve as the one primary action, Deny behind `RowActions` with a required note —
 * ADR 0009 §4, the exact precedent `TimeApprovalQueue.tsx` already set for the time
 * queue) and History — a decided request's outcome, reviewer's note and when it was
 * decided, filterable by status.
 *
 * TWO CARDS, ONE SCREEN. Pending is what needs a decision today; History is the record of
 * decisions already made. Splitting them into two screens would cost a nav step for a
 * farmer who just denied a request and wants to see it land; keeping the decided item in
 * the SAME queue it came from (rather than moving it to a second screen) would leave a
 * drained-looking list cluttered with yesterday's answered rows.
 */

const HOURS_LABEL: Record<string, string> = {
  vacation: "Vacation",
  sick: "Sick",
  personal: "Personal",
  unpaid: "Unpaid",
  other: "Other",
};

function formatDateRange(startDate: string, endDate: string): string {
  return startDate === endDate
    ? formatDateShort(startDate)
    : `${formatDateShort(startDate)} – ${formatDateShort(endDate)}`;
}

function formatReviewedAt(iso: string): string {
  return new Date(iso).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function TimeOff() {
  const pending = usePendingTimeOff();
  // Defaults to `null` — NO status chosen yet, so History fetches nothing and starts
  // genuinely empty rather than surfacing every outcome from the last 90 days unasked.
  // See useTimeOff.ts's own remarks on `useTimeOffHistory`: this is load-bearing, not
  // just tidy — a still-PENDING request's `id` can otherwise appear twice on one page
  // (once in the queue above, once in an eagerly-fetched History), which is exactly the
  // strict-mode collision `time-off.spec.ts` surfaced directly.
  const [historyFilter, setHistoryFilter] = useState<TimeOffStatus | "all" | null>(null);
  const history = useTimeOffHistory(historyFilter);

  const [busyId, setBusyId] = useState<Guid | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [denying, setDenying] = useState<Guid | null>(null);

  const statusFilterId = useId();

  async function decide(id: Guid, approved: boolean, note?: string): Promise<void> {
    setBusyId(id);
    setActionError(null);
    try {
      await demoApi.reviewTimeOff(id, approved ? { approved } : { approved, note });
      setDenying(null);
    } catch (err) {
      setActionError(
        problemMessage(err, "That decision didn't go through — the queue has been reloaded."),
      );
    } finally {
      setBusyId(null);
      // Always, including after a failure: a 409 means somebody else already decided
      // this row, and the only honest next state is a queue (and history) that
      // reflect that, not one still offering buttons for a decision already made
      // (mirrors TimeApprovalQueue.tsx's own `decide`).
      await pending.reload();
      await history.reload();
    }
  }

  const pendingEntries = pending.entries ?? [];
  const historyEntries = history.entries ?? [];

  return (
    <main className="time-off-page">
      <PageHeader title="Time off" subtitle="Review requests and see how past ones were decided." />

      <AreaTabs area="/setup" tabs={SETUP_TABS} label="Setup" />

      <section
        className="time-off-page__card"
        aria-labelledby="time-off-pending-heading"
        data-state={pending.status === "ready" && pendingEntries.length === 0 ? "empty" : pending.status}
      >
        <h2 id="time-off-pending-heading" className="time-off-page__card-title">
          Pending requests
        </h2>

        {pending.status === "loading" && <StateMessage tone="loading" title="Loading time-off requests…" />}
        {pending.status === "error" && (
          <StateMessage tone="error" title="Could not load pending requests." detail={pending.error ?? undefined} />
        )}
        {pending.status === "ready" && pendingEntries.length === 0 && (
          <StateMessage tone="empty" title="Nothing waiting on you." detail="Leave requests land here for review." />
        )}

        {actionError && (
          <p className="time-off-page__error" role="alert">
            {actionError}
          </p>
        )}

        {pending.status === "ready" && pendingEntries.length > 0 && (
          <ul className="time-off-page__list" aria-labelledby="time-off-pending-heading">
            {pendingEntries.map((request) => (
              <PendingRow
                key={request.id}
                request={request}
                busy={busyId === request.id}
                disabled={busyId !== null}
                denying={denying === request.id}
                onStartDeny={() => setDenying(request.id)}
                onCancelDeny={() => setDenying(null)}
                onDecide={decide}
              />
            ))}
          </ul>
        )}
      </section>

      <section
        className="time-off-page__card"
        aria-labelledby="time-off-history-heading"
        data-state={history.status === "ready" && historyEntries.length === 0 ? "empty" : history.status}
      >
        <div className="time-off-page__history-head">
          <h2 id="time-off-history-heading" className="time-off-page__card-title">
            History
          </h2>
          <label className="time-off-page__filter" htmlFor={statusFilterId}>
            Status
            <select
              id={statusFilterId}
              value={historyFilter ?? ""}
              onChange={(e) => {
                const next = e.target.value;
                setHistoryFilter(next === "" ? null : (next as TimeOffStatus | "all"));
              }}
            >
              <option value="">Choose a status</option>
              <option value="all">All</option>
              <option value="approved">Approved</option>
              <option value="denied">Denied</option>
            </select>
          </label>
        </div>

        {history.status === "loading" && <StateMessage tone="loading" title="Loading history…" />}
        {history.status === "error" && (
          <StateMessage tone="error" title="Could not load time-off history." detail={history.error ?? undefined} />
        )}
        {history.status === "ready" && historyFilter === null && (
          <StateMessage
            tone="empty"
            title="Choose a status to see decided requests."
            detail="History shows what was approved or denied, and why."
          />
        )}
        {history.status === "ready" && historyFilter !== null && historyEntries.length === 0 && (
          <StateMessage tone="empty" title="Nothing here yet." detail="Decided requests will show up here." />
        )}

        {history.status === "ready" && historyEntries.length > 0 && (
          <ul className="time-off-page__list" aria-labelledby="time-off-history-heading">
            {historyEntries.map((request) => (
              <HistoryRow key={request.id} request={request} />
            ))}
          </ul>
        )}
      </section>
    </main>
  );
}

function PendingRow({
  request,
  busy,
  disabled,
  denying,
  onStartDeny,
  onCancelDeny,
  onDecide,
}: {
  request: TimeOffRequest;
  busy: boolean;
  disabled: boolean;
  denying: boolean;
  onStartDeny: () => void;
  onCancelDeny: () => void;
  onDecide: (id: Guid, approved: boolean, note?: string) => Promise<void>;
}) {
  const [note, setNote] = useState("");
  const canDeny = note.trim().length > 0;

  return (
    <li className="time-off-page__row" data-time-off-id={request.id}>
      <span className="time-off-page__meta">
        <span className="time-off-page__name">{request.employeeName}</span>
        <span className="time-off-page__detail">
          {formatDateRange(request.startDate, request.endDate)} · {request.hoursRequested.toFixed(2)} hrs ·{" "}
          {HOURS_LABEL[request.kind] ?? request.kind}
        </span>
        {request.reason && <span className="time-off-page__reason">{request.reason}</span>}
      </span>

      <span className="time-off-page__actions">
        <button
          type="button"
          className="btn btn--primary time-off-page__btn"
          disabled={disabled}
          onClick={() => void onDecide(request.id, true)}
        >
          {busy ? "…" : "Approve"}
        </button>

        <RowActions onOpenChange={(open) => !open && onCancelDeny()}>
          {denying ? (
            <span className="time-off-page__deny-form">
              <label className="time-off-page__note-label">
                <span className="time-off-page__note-caption">Why not? The employee sees this.</span>
                <input
                  className="time-off-page__note"
                  type="text"
                  value={note}
                  autoFocus
                  maxLength={500}
                  placeholder="e.g. no coverage that week"
                  onChange={(e) => setNote(e.target.value)}
                />
              </label>
              <span className="time-off-page__deny-actions">
                <button
                  type="button"
                  className="btn btn--danger time-off-page__btn"
                  disabled={!canDeny || disabled}
                  onClick={() => void onDecide(request.id, false, note.trim())}
                >
                  {busy ? "…" : "Deny"}
                </button>
                <button type="button" className="btn btn--ghost time-off-page__btn" onClick={onCancelDeny}>
                  Cancel
                </button>
              </span>
            </span>
          ) : (
            <button type="button" className="btn btn--ghost time-off-page__btn" onClick={onStartDeny}>
              Deny
            </button>
          )}
        </RowActions>
      </span>
    </li>
  );
}

function HistoryRow({ request }: { request: TimeOffRequest }) {
  return (
    <li className="time-off-page__row" data-time-off-id={request.id} data-state={request.status}>
      <span className="time-off-page__meta">
        <span className="time-off-page__name">{request.employeeName}</span>
        <span className="time-off-page__detail">
          {formatDateRange(request.startDate, request.endDate)} · {request.hoursRequested.toFixed(2)} hrs ·{" "}
          {HOURS_LABEL[request.kind] ?? request.kind}
        </span>
        {request.reason && <span className="time-off-page__reason">{request.reason}</span>}
        {request.reviewNote && <span className="time-off-page__review-note">{request.reviewNote}</span>}
      </span>

      <span className="time-off-page__history-outcome">
        <span className="time-off-page__status">{request.status}</span>
        {request.reviewedAt && (
          <span className="time-off-page__reviewed-at">Reviewed {formatReviewedAt(request.reviewedAt)}</span>
        )}
      </span>
    </li>
  );
}
